import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Board, BoardRoute, BoardStop } from '../lib/types';
import { ampm, distance, hhmm, hours, nowSecondsIn, statusLabel, stopLabel, todayIn } from '../lib/format';
import { routeColor } from '../lib/route-color';
import { Delta } from './Delta';
import { Select } from '@allrouted/ui/Select';
import { KebabIcon, Menu, MenuToggle } from '@allrouted/ui/Menu';

/**
 * The routes panel, sitting under the map exactly as the incumbent has it.
 *
 * A grid of technicians on the left, a Gantt of their day on the right, both
 * scrolling together. Each row carries the two controls dispatchers actually
 * use: a padlock that protects a route from the next replan, and an eye that
 * takes it off the map when the picture gets crowded.
 *
 * Stops are drawn twice: hatched where the plan put them, solid where they
 * actually are. The gap is the drift.
 */


interface DragState {
  /*
   * A drag is one of two things, and the pair below says which.
   *
   * Moving a stop that is already on a route, or placing an order that is on no
   * route at all -- which starts in the unassigned panel, outside this
   * component. Everything between the press and the drop is identical, so they
   * share the machinery and part company only at the drop.
   */
  stopId: string | null;
  orderId: string | null;
  fromRouteId: string | null;
  label: string;
  targetRouteId: string | null;
  targetSequence: number | null;
  /** The clock time under the pointer, shown on the chip that follows it. */
  targetSeconds: number | null;
  /** The time the pointer is actually over, for a drag that sets a hold. */
  targetAt: number | null;
  /** Where the pointer is, so the chip can sit under it. */
  x: number;
  y: number;
  /** Where the press began, to tell a click from a drag. */
  originX: number;
  originY: number;
  /** Picked up from a route that is locked, so it is going nowhere. */
  fromLocked: boolean;
  fromVehicleName: string | null;
  /** The locked technician under the pointer, when that is why there is no target. */
  blockedBy: string | null;
}

/**
 * What the board is ordered by.
 *
 * `departure` is the server's own order and the default, because it is the
 * order the day actually happens in. The other three are the three columns a
 * dispatcher reads across -- the badge number, the vehicle, the driver.
 */
/**
 * The number on a route's badge, or null where it has none.
 *
 * One answer, because there used to be two. The badge fell back to the row's
 * position and the sort fell back to a very large number, so a vehicle with no
 * `idx` displayed as `1` purely for being first and sorted ahead of everything
 * in descending order -- which read as `1, 13, 12, 11 ...` and looked like the
 * column was sorting as text. It was not: it was two different ideas of what
 * an unnumbered route is worth.
 */
function routeNumberOf(route: BoardRoute): number | null {
  return route.routeNumber ?? route.vehicleIdx ?? null;
}

type SortKey = 'departure' | 'number' | 'name' | 'driver';
const SORT_KEYS: SortKey[] = ['departure', 'number', 'name', 'driver'];

/**
 * A column heading that sorts the board.
 *
 * Both arrows are always drawn, the active one solid and the other faint, so
 * the heading says what a click will do before it is clicked -- and `aria-sort`
 * says the same thing to a screen reader, which is the part a pair of glyphs
 * cannot carry on its own.
 */
function SortHeader({
  className,
  label,
  sortKey,
  sort,
  desc,
  onSort,
}: {
  className: string;
  label: string;
  sortKey: SortKey;
  sort: SortKey;
  desc: boolean;
  onSort: (key: SortKey) => void;
}) {
  const active = sort === sortKey;
  return (
    <button
      type="button"
      className={`rp-cell ${className} rp-sortable`}
      data-sorted={active ? (desc ? 'desc' : 'asc') : undefined}
      aria-sort={active ? (desc ? 'descending' : 'ascending') : 'none'}
      title={
        active
          ? desc
            ? `Sorted by ${label.toLowerCase()}, descending - click for ascending`
            : `Sorted by ${label.toLowerCase()}, ascending - click for descending`
          : `Sort by ${label.toLowerCase()}`
      }
      onClick={() => onSort(sortKey)}
    >
      {label}
      <svg className="rp-sort-arrows" width="8" height="12" viewBox="0 0 8 12" aria-hidden="true">
        <path className="rp-sort-up" d="M4 1L7 5H1z" />
        <path className="rp-sort-down" d="M4 11L1 7h6z" />
      </svg>
    </button>
  );
}

export function RoutesPanel({
  board,
  selectedRouteId,
  hiddenRouteIds,
  onSelectRoute,
  onSelectStop,
  onOpenRoute,
  onToggleHidden,
  onToggleLock,
  onLockAll,
  onHideAll,
  pendingDrag,
  onPendingDragTaken,
  onAssignOrder,
  onFocusOrder,
  onMoveStop,
  onDropMissed,
  onDropUnchanged,
  onDropBlocked,
  onHoldStop,
  onShiftDay,
}: {
  board: Board | null;
  selectedRouteId: string | null;
  hiddenRouteIds: Set<string>;
  onSelectRoute: (routeId: string) => void;
  onSelectStop: (routeId: string, stopId: string) => void;
  /** Open the route drawer: the whole day for one technician. */
  onOpenRoute: (routeId: string) => void;
  onToggleHidden: (routeId: string) => void;
  onToggleLock: (routeId: string, locked: boolean) => void;
  onLockAll?: (locked: boolean) => void;
  /**
   * Show or hide every route on the map at once.
   *
   * The counterpart to the per-row eye, and the quickest way to read a single
   * driver's day on a crowded map: hide all, then show the one. Without it a
   * thirteen-van board takes twelve clicks to get there.
   */
  onHideAll?: (hidden: boolean) => void;
  /**
   * An order picked up in the unassigned panel, handed over mid-press.
   *
   * The panel is a sibling, so it cannot reach this component's drag state. It
   * reports the press instead and this turns it into a drag, which keeps one
   * implementation of dropping rather than two that behave almost alike.
   */
  pendingDrag?: { orderId: string; label: string; x: number; y: number } | null;
  onPendingDragTaken?: () => void;
  onAssignOrder?: (orderId: string, target: string, toSequence: number) => Promise<void>;
  onFocusOrder?: (orderId: string) => void;
  onMoveStop?: (stopId: string, toRouteId: string, toSequence: number) => Promise<void>;
  /**
   * A working day dragged to a new start or end. `vehicleId` null means the
   * whole fleet, which is the ruler being dragged rather than one lane.
   */
  onShiftDay?: (
    vehicleId: string | null,
    edge: 'start' | 'end',
    seconds: number,
  ) => Promise<void>;
  /** Released somewhere that is not a route. */
  onDropMissed?: (label: string) => void;
  /**
   * Released somewhere that changes nothing.
   *
   * `earliest` is set when the drop asked for a time the route cannot reach --
   * the technician simply is not there yet -- so the message can say which.
   */
  onDropUnchanged?: (label: string, earliest?: number) => void;
  /**
   * Hold a stop until a given time, or release it with null.
   *
   * What a drag inside a lane means when it does not cross a neighbour.
   */
  onHoldStop?: (stopId: string, notBeforeSec: number | null) => Promise<void>;
  /**
   * A lock got in the way -- either the route it came from or the one it was
   * dropped on. `which` says so, because the two need different sentences.
   */
  onDropBlocked?: (label: string, vehicleName: string, which: 'source' | 'target') => void;
}) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);

  /*
   * How far past the day the ruler currently reaches, in seconds at each end.
   *
   * Grown while a shift handle is held against an edge -- see `edgeHold` below
   * -- and reset once the saved hours have caught up with where the drag went.
   */
  const [reach, setReach] = useState({ before: 0, after: 0 });

  /* The day itself: the earliest thing on the board and the latest. */
  const bounds = useMemo(() => {
    if (!board || board.routes.length === 0) return null;
    let start = Infinity;
    let end = -Infinity;
    for (const route of board.routes) {
      /*
       * The shift bounds count, not just the work. A day whose hours have been
       * widened past its first and last stop must still show both handles, or
       * the drag that widened it cannot be undone.
       */
      start = Math.min(start, route.actualStart ?? route.plannedStart, route.shiftStart);
      end = Math.max(end, route.projectedEnd ?? route.plannedEnd, route.shiftEnd);
      for (const stop of route.stops) {
        end = Math.max(end, stop.projectedDeparture ?? stop.plannedDeparture);
      }
    }
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
    return { from: Math.floor(start / 3600) * 3600, to: Math.ceil(end / 3600) * 3600 };
  }, [board]);

  /*
   * Once the day itself has moved, the propped-open ruler can relax.
   *
   * Dragging a shift out to 20:00 saves, the board comes back with the longer
   * day, and `bounds` now covers what the drag reached -- so the headroom is
   * doing nothing and is given back. Keyed on the day's own edges rather than
   * on the board, which changes every time a van reports a position and would
   * collapse the ruler mid-drag.
   */
  const holdingRef = useRef(false);
  useEffect(() => {
    /*
     * Not while a handle is held. The board changes every time a van reports a
     * position, and giving the headroom back in the middle of a drag would
     * snap the ruler shut under the pointer.
     */
    if (holdingRef.current) return;
    setReach((r) => (r.before === 0 && r.after === 0 ? r : { before: 0, after: 0 }));
  }, [bounds?.from, bounds?.to]);

  /*
   * An hour of empty ruler at each end, plus whatever a drag has opened up.
   *
   * The ruler used to be cut exactly to the day, which made its right edge both
   * the end of the day and the end of the track -- so a handle dragged to
   * 17:00 had nowhere further to go, and there was no way to say "work until
   * eight tonight". The ruler would happily have shown 20:00 once something
   * reached 20:00; nothing could make the first move.
   */
  const HEADROOM = 3600;
  const span = useMemo(() => {
    if (!bounds) return null;
    const from = bounds.from - HEADROOM - reach.before;
    const to = bounds.to + HEADROOM + reach.after;
    return { from, to, width: Math.max(3600, to - from) };
  }, [bounds, reach]);

  /*
   * Held against an edge, the ruler keeps growing.
   *
   * A pointer pressed to the far right stops producing move events, so the
   * growth is driven by a timer rather than by the pointer: a quarter hour
   * every tick for as long as it is held there. Each tick widens the span, so
   * the time under the unmoved pointer gets later and the preview follows it.
   */
  const [edgeHold, setEdgeHold] = useState<'start' | 'end' | null>(null);
  holdingRef.current = edgeHold !== null;
  useEffect(() => {
    if (!edgeHold) return;
    const id = window.setInterval(() => {
      setReach((r) =>
        edgeHold === 'end' ? { ...r, after: r.after + 900 } : { ...r, before: r.before + 900 },
      );
    }, 120);
    return () => window.clearInterval(id);
  }, [edgeHold]);

  /** The inverse of `pct`: where a pointer at this fraction of the track sits. */
  const secondsAtRatio = useCallback(
    (ratio: number) => (span ? span.from + ratio * span.width : 0),
    [span],
  );

  const resolveTarget = useCallback(
    (clientX: number, clientY: number) => {
      if (!board || !span) {
        return { routeId: null, sequence: null, seconds: null, at: null, blockedBy: null };
      }
      const row = document.elementFromPoint(clientX, clientY)?.closest<HTMLElement>('.rp-row');
      const routeId = row?.dataset.routeId ?? null;
      if (!routeId) return { routeId: null, sequence: null, seconds: null, at: null, blockedBy: null };

      const track = row?.querySelector<HTMLElement>('.rp-track');
      const route = board.routes.find((r) => laneKey(r) === routeId);
      if (!track || !route) {
        return { routeId, sequence: null, seconds: null, at: null, blockedBy: null };
      }

      /*
       * A locked lane is not a target.
       *
       * Reported as blocked rather than simply ignored: a drop that does
       * nothing and says nothing is the same experience as a drop that is
       * broken, and the dispatcher has a lock to undo if they meant it.
       */
      if (route.locked) {
        return { routeId: null, sequence: null, seconds: null, at: null, blockedBy: route.vehicleName };
      }

      const rect = track.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      const seconds = span.from + ratio * span.width;

      /*
       * Where it would land, decided by the gaps between stops rather than by
       * the instant under the pointer.
       *
       * Two things made reordering inside a lane a fight. The old rule counted
       * stops whose *arrival* was before the pointer, so the zone that meant
       * "after this one" began at the next chip's left edge -- on a twelve-hour
       * ruler the gap between two consecutive jobs is a few pixels, and the
       * target was effectively invisible. And it counted the dragged stop
       * itself, so moving a stop one place to the right inside its own route
       * resolved to the position it already had and did nothing at all.
       *
       * Now: the stop being dragged is excluded, and each remaining stop is
       * measured at its midpoint. Passing the middle of a chip means past it,
       * so every insertion point is a zone half a chip plus a gap wide --
       * forgiving enough to hit while moving.
       */
      const others = route.stops.filter((s) => s.id !== dragRef.current?.stopId);
      const before = others.filter((s) => {
        const from = s.projectedArrival ?? s.plannedArrival;
        const to = s.projectedDeparture ?? s.plannedDeparture;
        return (from + to) / 2 < seconds;
      }).length;

      /*
       * The caret sits on the boundary the stop would take, not under the
       * pointer -- so it shows the decision rather than the finger.
       */
      const previous = others[before - 1];
      const next = others[before];
      const edge = previous
        ? (previous.projectedDeparture ?? previous.plannedDeparture)
        : (route.plannedStart ?? seconds);
      const nextEdge = next ? (next.projectedArrival ?? next.plannedArrival) : edge;
      const caretAt = next && previous ? (edge + nextEdge) / 2 : next ? nextEdge : edge;

      /*
       * `seconds` is where the caret is drawn -- the boundary the stop would
       * take. `at` is the time under the pointer itself, which is what a drag
       * along a lane is asking for when it is not asking for a reorder.
       */
      return { routeId, sequence: before + 1, seconds: caretAt, at: seconds, blockedBy: null };
    },
    [board, span],
  );

  const endDrag = useCallback(async () => {
    const current = dragRef.current;
    dragRef.current = null;
    setDrag(null);
    if (!current) return;

    /*
     * A press that barely moved is a click.
     *
     * The drag began on pointerdown with no threshold, so opening a stop
     * meant picking it up and putting it down again -- and because the drop
     * resolved to the position it already had, the answer to a click was
     * "already there" rather than the stop's details. Five pixels is about
     * the slop in a deliberate click and well under a deliberate drag.
     */
    const travelled = Math.hypot(current.x - current.originX, current.y - current.originY);
    if (travelled < 5) {
      if (current.stopId && current.fromRouteId) {
        onSelectStop(current.fromRouteId, current.stopId);
      } else if (current.orderId) {
        /* A click on an unassigned row still means "show me this order". */
        onFocusOrder?.(current.orderId);
      }
      return;
    }

    /*
     * Picked up from a route that is locked. Checked after the click threshold,
     * so tapping a stop on a locked route still opens it -- a lock stops the
     * route changing, not the dispatcher reading it.
     */
    if (current.fromLocked) {
      onDropBlocked?.(current.label, current.fromVehicleName ?? 'That route', 'source');
      return;
    }

    /*
     * Released over a locked lane. A different sentence from missing the board
     * entirely, because there is something to do about it.
     */
    if (current.blockedBy) {
      onDropBlocked?.(current.label, current.blockedBy, 'target');
      return;
    }

    /*
     * Released somewhere that is not a route row -- the map, the header, off the
     * window entirely. Say so rather than leaving the dispatcher wondering
     * whether the drop worked.
     */
    if (!current.targetRouteId || current.targetSequence === null) {
      onDropMissed?.(current.label);
      return;
    }

    /*
     * An order arriving from the panel has nowhere it already is, so there is no
     * such thing as dropping it back where it was.
     */
    if (current.orderId && !current.stopId) {
      await onAssignOrder?.(current.orderId, current.targetRouteId, current.targetSequence);
      return;
    }
    if (!current.stopId || !onMoveStop) return;

    /*
     * Dropped in its own place, which is a request to move it in time.
     *
     * Dragging inside a lane could only ever reorder, so letting go anywhere
     * that did not cross a neighbour did nothing at all -- and "this one starts
     * at four, I want it at half four" is a thing a dispatcher asks for far
     * more often than a reshuffle. The same gesture now does both, decided by
     * whether the drop crossed another stop: past one, it is a reorder; inside
     * the gap, it is a hold on the arrival.
     *
     * Dropping it back before where the route would reach it anyway clears the
     * hold, so the way to undo is the way you made it.
     */
    const here = board?.routes
      .find((r) => laneKey(r) === current.fromRouteId)
      ?.stops.find((s) => s.id === current.stopId);
    const sameSlot =
      current.targetRouteId === current.fromRouteId && here?.sequence === current.targetSequence;

    if (sameSlot) {
      if (current.targetAt === null) {
        onDropUnchanged?.(current.label);
        return;
      }
      /* Quarter hours, as everywhere a dispatcher sets a time by dragging. */
      const wanted = Math.round(current.targetAt / 900) * 900;

      /*
       * The earliest the technician could reach this stop if nothing held it:
       * whenever they leave the one before, plus the drive in.
       *
       * Release is measured against that, not against the hold itself. Judged
       * against the hold, nudging a held stop even slightly earlier read as
       * "let it go" and threw the hold away -- somebody who had just pinned a
       * job to four o'clock and reached over to adjust it to a quarter past
       * found it back at two in the afternoon.
       */
      const lane = board?.routes.find((r) => laneKey(r) === current.fromRouteId);
      const seq = here?.sequence ?? 1;
      const before = lane?.stops.find((st) => st.sequence === seq - 1);
      const natural =
        (before?.plannedDeparture ?? lane?.plannedStart ?? 0) + (here?.travelSeconds ?? 0);

      if (wanted <= natural) {
        /* Back to where the route reaches it anyway: nothing is holding it now. */
        if (here?.notBefore != null) {
          await onHoldStop?.(current.stopId, null);
        } else {
          onDropUnchanged?.(current.label, natural);
        }
        return;
      }
      await onHoldStop?.(current.stopId, wanted);
      return;
    }

    await onMoveStop(current.stopId, current.targetRouteId, current.targetSequence);
  }, [
    board,
    onMoveStop,
    onAssignOrder,
    onDropMissed,
    onDropUnchanged,
    onDropBlocked,
    onHoldStop,
    onSelectStop,
    onFocusOrder,
  ]);

  /*
   * A drag is driven from the window, not from the panel.
   *
   * Two reasons, both of which made dragging look broken. Handlers attached
   * conditionally on `drag` do not exist until React has re-rendered, so the
   * moves at the very start of a quick drag landed on nothing. And handlers on
   * the panel only see a release that happens over the panel -- let go over the
   * map and the pointerup was never heard, so the drag stayed live forever and
   * the next click did something surprising.
   */
  /*
   * The two things a move needs, read through refs.
   *
   * They close over `board`, which changes on every event the live feed
   * delivers. Putting them in the effect's dependencies tore the listeners
   * down and rebuilt them mid-drag -- see below for why that was fatal.
   */
  const resolveRef = useRef(resolveTarget);
  resolveRef.current = resolveTarget;
  const endRef = useRef(endDrag);
  endRef.current = endDrag;
  const chipRef = useRef<HTMLDivElement | null>(null);

  /*
   * Attached once per drag, and only once.
   *
   * This effect used to depend on `drag`, and its own move handler called
   * `setDrag` on every pointermove -- so every few milliseconds all four
   * window listeners were removed and re-added. Two consequences, both of
   * which read as the drag being broken rather than as a subtle bug:
   *
   * A `pointerup` landing in the gap between teardown and re-attach was never
   * heard, so the drag stayed live after the mouse was released and the next
   * click dropped a stop somewhere nobody asked for.
   *
   * And every pointermove re-rendered thirteen lanes and every stop on them,
   * which on a full board is thousands of nodes per second of pointer motion.
   *
   * So: a boolean dependency, refs for everything that changes, and the
   * following chip moved by writing to its style rather than through React.
   */
  /*
   * Adopt a press that began in the unassigned panel.
   *
   * The pointer is already down and already captured by the window listeners
   * below, so all this has to do is give the drag an identity. Taken once and
   * reported back, so a re-render does not start it again.
   */
  useEffect(() => {
    if (!pendingDrag) return;
    dragRef.current = {
      stopId: null,
      orderId: pendingDrag.orderId,
      /* An unassigned order sits on no route, so no lock can hold it back. */
      fromLocked: false,
      fromVehicleName: null,
      blockedBy: null,
      fromRouteId: null,
      label: pendingDrag.label,
      targetRouteId: null,
      targetSequence: null,
      targetSeconds: null,
      targetAt: null,
      x: pendingDrag.x,
      y: pendingDrag.y,
      originX: pendingDrag.x,
      originY: pendingDrag.y,
    };
    setDrag(dragRef.current);
    onPendingDragTaken?.();
  }, [pendingDrag, onPendingDragTaken]);

  const dragging = drag !== null;
  useEffect(() => {
    if (!dragging) return;

    const onMove = (event: PointerEvent) => {
      const current = dragRef.current;
      if (!current) return;
      event.preventDefault();

      /* The chip follows the finger without going through a render. */
      const chip = chipRef.current;
      if (chip) {
        chip.style.left = `${event.clientX}px`;
        chip.style.top = `${event.clientY}px`;
      }

      const { routeId, sequence, seconds, at, blockedBy } = resolveRef.current(
        event.clientX,
        event.clientY,
      );
      dragRef.current = { ...current, targetRouteId: routeId, targetSequence: sequence,
        targetSeconds: seconds, targetAt: at, blockedBy, x: event.clientX, y: event.clientY };

      /*
       * Only when the destination actually changes. Sliding within one gap
       * does not move anything on screen, and re-rendering to say so is the
       * expensive half of a drag.
       */
      if (
        routeId !== current.targetRouteId ||
        sequence !== current.targetSequence ||
        blockedBy !== current.blockedBy
      ) {
        setDrag(dragRef.current);
      }
    };

    const onUp = () => void endRef.current();
    const onKey = (event: KeyboardEvent) => {
      // Escape abandons the drag without moving anything.
      if (event.key !== 'Escape') return;
      dragRef.current = null;
      setDrag(null);
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, [dragging]);

  /*
   * A minute is the resolution the ruler is labelled at, so tick at that and
   * no faster. Without this the "now" line would only move when an SSE event
   * happened to re-render the board, which on a quiet afternoon is never.
   */
  const [minuteTick, setMinuteTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setMinuteTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, []);

  /*
   * What the rows are ordered by.
   *
   * Departure is the incumbent's order and the default: the board reads top to
   * bottom in the order the day happens. Its cost is that a drag which retimes
   * a route can move the row out from under the hand that dragged it -- drop a
   * stop on a van and its departure shifts into the 07:30 crowd, where the
   * tie-break is the vehicle's name, and a route five rows down lands at the
   * top. Route number never moves, which is what somebody rearranging a busy
   * board wants.
   *
   * Remembered per browser. It is a working preference, not a setting anybody
   * should have to find twice, and it belongs to the person rather than to the
   * territory.
   */
  /*
   * Whether vehicles that cannot work today are listed.
   *
   * Only those: a vehicle switched off, or taken out for the day by a calendar
   * exception. An available vehicle with nothing on it always has its lane --
   * that lane is where a dispatcher drags work the solver did not give it, and
   * hiding it made a van vanish the moment its last order was taken off.
   *
   * Hidden by default; the switch says how many there are. Remembered under a
   * new key: the old one meant "show empty lanes", and carrying it over would
   * have switched vehicles that are off onto the board of anyone who had it on.
   */
  const [showIdle, setShowIdle] = useState<boolean>(() => {
    try {
      return localStorage.getItem('allrouted.routes.unavailable') === 'show';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('allrouted.routes.unavailable', showIdle ? 'show' : 'hide');
    } catch {
      /* Private windows: the default is fine. */
    }
  }, [showIdle]);

  /*
   * The column and the direction as one value, not two.
   *
   * They were two, and toggling the direction from inside the column's own
   * updater quietly did nothing: React calls an updater twice in development,
   * so a nested `setSortDesc((d) => !d)` flipped and flipped back. Held
   * together, one click is one update and there is nothing to double.
   */
  const [sorting, setSorting] = useState<{ key: SortKey; desc: boolean }>(() => {
    try {
      const saved = localStorage.getItem('sw.routes.sort');
      return {
        key: SORT_KEYS.includes(saved as SortKey) ? (saved as SortKey) : 'departure',
        desc: localStorage.getItem('sw.routes.sortDir') === 'desc',
      };
    } catch {
      /* Private windows and blocked site data: the default is fine. */
      return { key: 'departure', desc: false };
    }
  });
  const { key: sort, desc: sortDesc } = sorting;
  useEffect(() => {
    try {
      localStorage.setItem('sw.routes.sort', sort);
      localStorage.setItem('sw.routes.sortDir', sortDesc ? 'desc' : 'asc');
    } catch {
      /* Not being able to remember it is not a reason to fail. */
    }
  }, [sort, sortDesc]);

  /**
   * A click on a column heading.
   *
   * The same gesture a data table has: the first click sorts by that column
   * ascending, a second reverses it. Changing column always starts ascending
   * rather than keeping the previous direction -- inheriting it means a click
   * on Driver can produce a reverse-alphabetical list for no visible reason.
   */
  const sortBy = useCallback((key: SortKey) => {
    setSorting((current) =>
      current.key === key ? { key, desc: !current.desc } : { key, desc: false },
    );
  }, []);

  /*
   * Departure order is the server's own, so it is left alone rather than
   * re-sorted into the same thing -- the server breaks ties by name and
   * repeating that here would be two places to keep in step.
   */
  /*
   * Vehicles that cannot work today. A route with stops on it is listed
   * whatever its vehicle says -- the work is there and has to be seen.
   */
  const isUnavailable = (r: BoardRoute) => r.available === false && r.stops.length === 0;
  const idleCount = (board?.routes ?? []).filter(isUnavailable).length;

  const orderedRoutes = useMemo(() => {
    if (!board) return [];
    /*
     * Filtered before sorting, not after: an empty lane has no departure to
     * sort by and would otherwise decide where the block of them lands.
     */
    const visible = showIdle ? board.routes : board.routes.filter((r) => !isUnavailable(r));
    /*
     * Ascending departure is the server's own order, so it is handed back
     * untouched -- re-sorting it here would be a second copy of the server's
     * tie-breaking to keep in step.
     */
    if (sort === 'departure' && !sortDesc) return visible;

    const direction = sortDesc ? -1 : 1;
    return [...visible].sort((a, b) => {
      switch (sort) {
        case 'departure':
          return (
            (a.plannedStart - b.plannedStart ||
              a.vehicleName.localeCompare(b.vehicleName)) * direction
          );
        case 'name':
          return a.vehicleName.localeCompare(b.vehicleName) * direction;
        case 'driver': {
          /*
           * A vehicle with nobody in it sorts last whichever way the arrow
           * points. "No driver" is not a name that belongs at either end of an
           * alphabet, and it is the row most likely to need attention.
           */
          const an = a.technicianName;
          const bn = b.technicianName;
          if (!an || !bn) return an === bn ? 0 : an ? -1 : 1;
          return an.localeCompare(bn) * direction;
        }
        default: {
          /*
           * By the number on the badge, so the rows read in the order the
           * numbers do -- including on an imported day, where the numbering is
           * the other system's and ours would put the rows in a different
           * order from the screen this is being compared against.
           *
           * A route with no number sorts last whichever way the arrow points.
           * A sentinel multiplied by the direction does the opposite of that in
           * descending order, which is the bug this replaced.
           */
          const ai = routeNumberOf(a);
          const bi = routeNumberOf(b);
          if (ai === null || bi === null) {
            return ai === bi ? 0 : ai === null ? 1 : -1;
          }
          return (ai - bi || a.vehicleName.localeCompare(b.vehicleName)) * direction;
        }
      }
    });
  }, [board, sort, sortDesc, showIdle]);

  const totals = useMemo(() => {
    if (!board) return null;
    const used = board.routes.filter((r) => r.stops.length > 0).length;
    return { used, free: board.routes.length - used, all: board.routes.length };
  }, [board]);

  /*
   * Idle lanes are not routes yet and cannot be locked, so they do not get a
   * say in whether the board counts as locked.
   */
  const lockable = (board?.routes ?? []).filter((r) => r.id !== null);
  const allLocked = lockable.length > 0 && lockable.every((r) => r.locked);
  /*
   * Only routes can be hidden, for the same reason only routes can be locked:
   * an idle lane has nothing drawn on the map to hide.
   */
  const allHidden =
    lockable.length > 0 && lockable.every((r) => r.id !== null && hiddenRouteIds?.has(r.id));

  if (!board || !span) {
    return (
      <section className="routes-panel">
        <div className="rp-head">
          <h2>Routes</h2>
        </div>
        <div className="empty">No routes planned for this day yet.</div>
      </section>
    );
  }

  const hourCount = Math.max(1, Math.round(span.width / 3600));
  const pct = (seconds: number) => ((seconds - span.from) / span.width) * 100;
  void minuteTick; /* read so the tick re-renders this line, nothing more */
  const now = nowSecondsIn(board.territory.timezone);
  const colorOf = routeColor;

  return (
    <section className="routes-panel">
      <div className="rp-head">
        <h2>Routes</h2>
        <span className="count-pill">{orderedRoutes.length}</span>

        {/*
          * The menu the incumbent puts beside this title. It carries one switch
          * so far -- the empty lanes -- because that is the one that was asked
          * for; the rest of theirs (columns, grouping, percentages) is not here
          * and a half-built menu claiming otherwise would be worse than none.
          */}
        <Menu label="Routes options" icon={<KebabIcon />} className="rp-kebab">
          <MenuToggle
            label={`Unavailable vehicles (${idleCount})`}
            hint="Vehicles switched off, or off for this day"
            checked={showIdle}
            onChange={setShowIdle}
          />
        </Menu>
        <span className="rp-hint">
          {drag
            ? drag.fromLocked
              ? `${drag.fromVehicleName ?? 'That route'} is locked — unlock it to move this stop`
              : drag.blockedBy
                ? `${drag.blockedBy} is locked — unlock to drop work there`
                : drag.targetRouteId
                  ? `Moving ${drag.label} — drop to place it here`
                  : `Moving ${drag.label} — drop on a technician, or press Escape`
            : 'Drag a stop to reschedule it, or an unassigned order onto a technician'}
        </span>
        <div style={{ flex: 1 }} />

        {/*
          * Beside the totals rather than above the ID column: it orders the
          * whole board, not that one field.
          */}
        <label className="rp-sort">
          <span>Sort</span>
          <Select
            value={sort}
            options={[
              { value: 'departure', label: 'Departure time', hint: 'the order the day happens' },
              { value: 'number', label: 'Route number', hint: 'fixed; rows never move' },
              { value: 'name', label: 'Vehicle name' },
              { value: 'driver', label: 'Driver' },
            ]}
            /*
             * Picked here or by clicking the column heading -- one piece of
             * state either way, so the arrow in the heading and this box can
             * never disagree about what the board is sorted by.
             */
            onChange={(v) =>
              setSorting({
                key: SORT_KEYS.includes(v as SortKey) ? (v as SortKey) : 'departure',
                desc: false,
              })
            }
          />
        </label>

        {totals ? (
          <span className="rp-totals">
            {/* Their wording: "Vehicles: Used 7 · Free 3". */}
            Vehicles: Used <b>{totals.used}</b> · Free <b>{totals.free}</b>
          </span>
        ) : null}
      </div>

      <div className="rp-body" data-dragging={drag ? 'true' : undefined}>
        <div className="rp-grid">
          <div className="rp-header-row">
            {/*
              * The three headings sort the board, as a data table's do. The
              * numbering column sorts on "number" rather than a key of its
              * own: the badge is the route number, so two keys for it would be
              * two names for one ordering.
              */}
            <SortHeader
              className="rp-id"
              label="ID"
              sortKey="number"
              sort={sort}
              desc={sortDesc}
              onSort={sortBy}
            />
            <SortHeader
              className="rp-name"
              label="Name"
              sortKey="name"
              sort={sort}
              desc={sortDesc}
              onSort={sortBy}
            />
            <SortHeader
              className="rp-driver"
              label="Driver"
              sortKey="driver"
              sort={sort}
              desc={sortDesc}
              onSort={sortBy}
            />
            <div className="rp-cell rp-violations" title="Warnings">
              <WarningIcon />
            </div>
            <div className="rp-cell rp-icon" title="Open the full route">
              <RouteDetailIcon />
            </div>
            {/*
              * The column header doubles as lock-everything.
              *
              * A replan leaves locked routes alone, so this is how a dispatcher
              * protects a board they are happy with -- lock all, unlock the one
              * driver that needs redoing, replan. Filled when every route is
              * locked, so the icon answers "are we protected?" as well as
              * offering the action.
              */}
            <div
              className="rp-cell rp-icon"
              data-action={onLockAll ? 'true' : undefined}
              data-lock={allLocked ? 'on' : 'off'}
              title={
                onLockAll
                  ? allLocked
                    ? 'Every route is locked — click to unlock all'
                    : 'Lock every route against replanning'
                  : 'Lock against replanning'
              }
              onClick={onLockAll ? () => onLockAll(!allLocked) : undefined}
            >
              <LockIcon locked={allLocked} />
            </div>
            {/*
              * And the eye hides them all, as the padlock locks them all. It
              * was inert -- a column heading that looked exactly like the
              * twelve clickable eyes beneath it and did nothing.
              */}
            <div
              className="rp-cell rp-icon"
              data-action={onHideAll ? 'true' : undefined}
              title={
                onHideAll
                  ? allHidden
                    ? 'Every route is hidden — click to show all on the map'
                    : 'Hide every route from the map'
                  : 'Show on map'
              }
              onClick={onHideAll ? () => onHideAll(!allHidden) : undefined}
            >
              <EyeIcon hidden={allHidden} />
            </div>
            <div className="rp-hours">
              {/*
                * The fleet's day, draggable at both ends.
                *
                * The earliest start and the latest end across the whole board:
                * pulling either moves every active vehicle for this date, which
                * is what "we start half an hour early on Mondays" means. One
                * request, so it cannot half-apply.
                */}
              {onShiftDay && board.routes.length > 0 ? (
                <FleetShift
                  routes={board.routes}
                  pct={pct}
                  secondsAtRatio={secondsAtRatio}
                  onShiftDay={onShiftDay}
                  onEdgeHold={setEdgeHold}
                />
              ) : null}
              {Array.from({ length: hourCount }, (_, i) => (
                <div key={i} className="rp-hour">
                  {hhmm(span.from + i * 3600)}
                </div>
              ))}
              {/*
                * Now, labelled once on the ruler and drawn as a dashed line down
                * every row. On a board whose whole job is watching a day run,
                * this is the cheapest orientation there is: late and early are
                * read against it without doing arithmetic.
                */}
              {pct(now) >= 0 && pct(now) <= 100 ? (
                <span className="rp-now-label" style={{ left: `${pct(now)}%` }}>
                  {hhmm(now)}
                </span>
              ) : null}
            </div>
          </div>

          {orderedRoutes.map((route, i) => (
            <Row
              key={laneKey(route)}
              route={route}
              unit={board.territory.distanceUnit}
              index={i}
              color={colorOf(route, i)}
              onShiftDay={onShiftDay}
              secondsAtRatio={secondsAtRatio}
              pct={pct}
              now={now}
              isToday={board.date === todayIn(board.territory.timezone)}
              selected={laneKey(route) === selectedRouteId}
              hidden={hiddenRouteIds.has(laneKey(route))}
              isDropTarget={drag?.targetRouteId === laneKey(route)}
              dropAt={
                drag && drag.targetRouteId === laneKey(route) && drag.targetSeconds !== null
                  ? pct(drag.targetSeconds)
                  : null
              }
              dropLabel={drag && drag.targetRouteId === laneKey(route) ? drag.label : null}
              draggingStopId={drag?.stopId ?? null}
              draggable={Boolean(onMoveStop)}
              onSelectRoute={onSelectRoute}
              onSelectStop={onSelectStop}
              onOpenRoute={onOpenRoute}
              onEdgeHold={setEdgeHold}
              onToggleHidden={onToggleHidden}
              onToggleLock={onToggleLock}
              onDragStart={(event, stop) => {
                if (!onMoveStop || stop.status !== 'pending') return;
                /*
                 * No setPointerCapture. The window listeners see every move
                 * wherever the pointer goes, and capturing on an element React
                 * re-renders during the drag is a good way to lose it.
                 */
                event.preventDefault();
                const next: DragState = {
                  stopId: stop.id,
                  orderId: null,
                  fromLocked: route.locked,
                  fromVehicleName: route.vehicleName,
                  blockedBy: null,
                  /* The lane key, matching what the drop resolves to. */
                  fromRouteId: laneKey(route),
                  label: stop.orderName,
                  targetRouteId: laneKey(route),
                  targetSequence: stop.sequence,
                  targetSeconds: null,
                  targetAt: null,
                  x: event.clientX,
                  y: event.clientY,
                  originX: event.clientX,
                  originY: event.clientY,
                };
                dragRef.current = next;
                setDrag(next);
              }}
            />
          ))}
        </div>
      </div>

      {/*
       * The stop, under the pointer.
       *
       * Highlighting the destination row told a dispatcher where a drop would
       * land but never showed anything actually moving, so the gesture felt
       * like nothing was happening until it was over. This follows the finger.
       */}
      {drag ? (
        <div
          ref={chipRef}
          className="rp-drag-chip"
          data-armed={drag.targetRouteId ? 'true' : undefined}
          style={{ left: drag.x, top: drag.y }}
        >
          {drag.label}
          {drag.targetSeconds !== null ? <b>{hhmm(Math.round(drag.targetSeconds))}</b> : null}
        </div>
      ) : null}
    </section>
  );
}


/**
 * Open a hover popover upward when there is no room for it below.
 *
 * The panel scrolls, so a popover on the last route hangs past the bottom of
 * the scroll container and is clipped -- and unlike a row in the middle, there
 * is nothing further down to scroll to, so it cannot be recovered.
 *
 * Measured rather than guessed with `:nth-last-child`. Which rows sit near the
 * bottom depends on how tall the operator has dragged the panel and how far
 * they have scrolled, and a rule that flips the last two rows is wrong for a
 * territory with two routes -- there the last row is also the first, and the
 * popover would be clipped at the top instead.
 *
 * Runs on hover rather than on render, so nothing is measured that nobody is
 * looking at.
 */

/**
 * What identifies a lane on the board.
 *
 * A route has an id. A vehicle the solver did not use has no route at all, and
 * is shown anyway so a dispatcher can drop work on it -- so the lane is keyed
 * by the vehicle until the move that creates its route. The `v:` prefix is what
 * the move handler reads to tell the two apart.
 */
export const laneKey = (route: { id: string | null; vehicleId: string }): string =>
  route.id ?? `v:${route.vehicleId}`;

/*
 * Hover cards -- a stop's fact sheet, a route's violations -- are drawn in one
 * floating box at the end of the document, not inside the row.
 *
 * Inside, they were clipped by the panel's scroll box and by any narrow or
 * resized column: flipping up only helped when there was room above *within the
 * panel*. Out here they are measured against the window, open below or above
 * whichever has more room, slide sideways to stay on screen, and scroll when
 * even that is not enough. The card's markup is still rendered in the row
 * (hidden) and copied across on hover, so its content stays in React's hands.
 */
let floatingTip: HTMLDivElement | null = null;

function showTip(anchor: HTMLElement, selector: string): void {
  const source = anchor.querySelector<HTMLElement>(selector);
  if (!source) return;
  if (!floatingTip) {
    floatingTip = document.createElement('div');
    floatingTip.setAttribute('role', 'tooltip');
    document.body.appendChild(floatingTip);
    /* Anything that moves the page makes the position wrong; just put it away. */
    window.addEventListener('scroll', hideTip, true);
    window.addEventListener('resize', hideTip);
  }
  const tip = floatingTip;
  tip.className = `${source.className} rp-tip-floating`;
  tip.innerHTML = source.innerHTML;
  tip.style.cssText = 'position:fixed;top:0;left:0;display:flex;visibility:hidden;transform:none;';

  const gap = 7;
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const a = anchor.getBoundingClientRect();
  const width = tip.offsetWidth;
  const natural = tip.scrollHeight;
  const below = vh - a.bottom - gap - margin;
  const above = a.top - gap - margin;
  const up = natural > below && above > below;
  const room = Math.max(120, up ? above : below);
  const height = Math.min(natural, room);
  const left = Math.min(Math.max(margin, a.left + a.width / 2 - width / 2), vw - width - margin);

  tip.style.left = `${Math.max(margin, left)}px`;
  tip.style.top = `${up ? a.top - gap - height : a.bottom + gap}px`;
  tip.style.maxHeight = `${room}px`;
  tip.style.overflowY = natural > room ? 'auto' : 'visible';
  tip.style.visibility = 'visible';
}

function hideTip(): void {
  if (floatingTip) floatingTip.style.display = 'none';
}

/** Kept under its old name: callers hover an anchor and name the card inside it. */
function flipToFit(anchor: HTMLElement, selector: string): void {
  showTip(anchor, selector);
}


/**
 * The hours a vehicle may work today, and the two handles that change them.
 *
 * Their board lets a dispatcher pull a day earlier or later by its edges, which
 * is a different act from editing the vehicle: "Kyle starts at seven today"
 * rather than "Kyle starts at seven". The server writes it as a calendar
 * exception for the date, and the standing shift is untouched.
 *
 * Nothing re-plans on release. The day re-times on the next build, as theirs
 * does -- which is what makes a drag cheap enough to try and undo.
 */

/**
 * The whole fleet's working day, dragged from the ruler.
 *
 * Shows the earliest start and the latest end on the board, because that is the
 * shape of the day a dispatcher is looking at. Moving either end writes the
 * same time to every active vehicle for this date -- which is what an operator
 * means by "we start early today", and is one request rather than thirteen that
 * can half-fail.
 */
function FleetShift({
  routes,
  pct,
  secondsAtRatio,
  onShiftDay,
  onEdgeHold,
}: {
  routes: BoardRoute[];
  pct: (seconds: number) => number;
  secondsAtRatio: (ratio: number) => number;
  onShiftDay: (vehicleId: string | null, edge: 'start' | 'end', seconds: number) => Promise<void>;
  onEdgeHold?: (edge: 'start' | 'end' | null) => void;
}) {
  const [preview, setPreview] = useState<{ edge: 'start' | 'end'; seconds: number } | null>(null);

  const earliest = Math.min(...routes.map((r) => r.shiftStart));
  const latest = Math.max(...routes.map((r) => r.shiftEnd));
  const start = preview?.edge === 'start' ? preview.seconds : earliest;
  const end = preview?.edge === 'end' ? preview.seconds : latest;

  const secondsAt = (clientX: number, el: HTMLElement): number => {
    const rect = el.closest<HTMLElement>('.rp-hours')!.getBoundingClientRect();
    const raw = (clientX - rect.left) / rect.width;
    const ratio = Math.min(1, Math.max(0, raw));
    /* The same edge rule as a single lane: held past the end, the ruler grows. */
    onEdgeHold?.(raw >= 0.995 ? 'end' : raw <= 0.005 ? 'start' : null);
    /* Quarter hours, so a drag lands on a time somebody would say out loud. */
    return Math.round(secondsAtRatio(ratio) / 900) * 900;
  };

  const handle = (edge: 'start' | 'end') => ({
    className: 'rp-fleet-grip',
    'data-edge': edge,
    style: { left: `${pct(edge === 'start' ? start : end)}%` },
    title:
      edge === 'start'
        ? 'Start of the day for every vehicle — drag to change it for this date'
        : 'End of the day for every vehicle — drag to change it for this date',
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      setPreview({ edge, seconds: secondsAt(event.clientX, event.currentTarget) });
    },
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
      if (!preview || preview.edge !== edge) return;
      setPreview({ edge, seconds: secondsAt(event.clientX, event.currentTarget) });
    },
    onPointerUp: (event: React.PointerEvent<HTMLElement>) => {
      event.currentTarget.releasePointerCapture(event.pointerId);
      onEdgeHold?.(null);
      const settled = preview;
      setPreview(null);
      const was = edge === 'start' ? earliest : latest;
      if (!settled || settled.seconds === was) return;
      void onShiftDay(null, edge, settled.seconds);
    },
  });

  return (
    <>
      <span {...handle('start')} />
      <span {...handle('end')} />
      {preview ? (
        <span className="rp-shift-readout" style={{ left: `${pct(preview.seconds)}%` }}>
          {hhmm(preview.seconds)}
        </span>
      ) : null}
    </>
  );
}

function ShiftBand({
  route,
  color,
  pct,
  secondsAtRatio,
  onShiftDay,
  onEdgeHold,
}: {
  route: BoardRoute;
  color: string;
  pct: (seconds: number) => number;
  secondsAtRatio: (ratio: number) => number;
  onShiftDay?: (vehicleId: string | null, edge: 'start' | 'end', seconds: number) => Promise<void>;
  /** Pressed against an end of the ruler, so it should grow that way. */
  onEdgeHold?: (edge: 'start' | 'end' | null) => void;
}) {
  /* What the handle is showing mid-drag, before the server has been told. */
  const [preview, setPreview] = useState<{ edge: 'start' | 'end'; seconds: number } | null>(null);

  /*
   * What the handle was let go at, held until the board catches up.
   *
   * Releasing used to clear the preview immediately, so the arrow snapped back
   * to the old hours and then jumped out again a moment later when the save
   * landed -- a visible elastic recoil on every drag. The drop is already
   * decided by then; the only thing still outstanding is the server agreeing,
   * and there is no reason to animate the wait.
   */
  const [settledAt, setSettledAt] = useState<{ edge: 'start' | 'end'; seconds: number } | null>(
    null,
  );

  /*
   * Let go of the optimistic value once the board actually carries it -- or
   * carries something else, which is the server having refused or adjusted it.
   * Either way the truth has arrived and the local copy has nothing to add.
   */
  useEffect(() => {
    if (!settledAt) return;
    const live = settledAt.edge === 'start' ? route.shiftStart : route.shiftEnd;
    if (live === settledAt.seconds) setSettledAt(null);
  }, [settledAt, route.shiftStart, route.shiftEnd]);

  const held = (edge: 'start' | 'end'): number | null =>
    preview?.edge === edge ? preview.seconds : settledAt?.edge === edge ? settledAt.seconds : null;

  const start = held('start') ?? route.shiftStart;
  const end = held('end') ?? route.shiftEnd;
  const left = pct(start);
  const width = Math.max(0, pct(end) - left);

  const drag = (edge: 'start' | 'end') => (event: React.PointerEvent<HTMLElement>) => {
    if (!onShiftDay) return;
    event.preventDefault();
    event.stopPropagation();
    const track = event.currentTarget.closest<HTMLElement>('.rp-track');
    if (!track) return;
    const rect = track.getBoundingClientRect();
    event.currentTarget.setPointerCapture(event.pointerId);

    /*
     * Quarter hours. A dispatcher moving a shift means half past seven, not
     * 07:28:13, and snapping is what makes a pixel-accurate drag produce a time
     * somebody would say out loud.
     */
    const at = (clientX: number): number => {
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      return Math.round(secondsAtRatio(ratio) / 900) * 900;
    };

    setPreview({ edge, seconds: at(event.clientX) });
  };

  const move = (edge: 'start' | 'end') => (event: React.PointerEvent<HTMLElement>) => {
    if (!preview || preview.edge !== edge) return;
    const track = event.currentTarget.closest<HTMLElement>('.rp-track');
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const raw = (event.clientX - rect.left) / rect.width;
    const ratio = Math.min(1, Math.max(0, raw));
    /*
     * Past the end of the track, so there is nowhere left to drag to. Say so
     * and the ruler grows; the time under this unmoved pointer gets later each
     * tick and the preview below follows it.
     */
    onEdgeHold?.(raw >= 0.995 ? 'end' : raw <= 0.005 ? 'start' : null);
    setPreview({ edge, seconds: Math.round(secondsAtRatio(ratio) / 900) * 900 });
  };

  const release = (edge: 'start' | 'end') => async (event: React.PointerEvent<HTMLElement>) => {
    event.currentTarget.releasePointerCapture(event.pointerId);
    onEdgeHold?.(null);
    const settled = preview;
    setPreview(null);
    if (!settled || !onShiftDay) return;
    const was = edge === 'start' ? route.shiftStart : route.shiftEnd;
    if (settled.seconds === was) return;
    /* Hold the new position on screen while the save is in flight. */
    setSettledAt(settled);
    await onShiftDay(route.vehicleId, edge, settled.seconds);
  };

  /*
   * The journey: an arrow out, a dashed thread, an arrow home.
   *
   * Drawn here rather than beside the stops because the arrows are also the
   * handles. A dispatcher wanting the day to finish earlier points at the
   * arrow -- it is the end of the work, visibly -- and the first version put
   * the handles on the edges of the pale hours band instead. At the start of a
   * day those two coincide and it felt right; at the end they diverge by
   * however much slack the route has, and the arrow did nothing at all.
   *
   * So both are draggable, and both set the same thing: the hours for this
   * date. The arrow is where the eye goes; the band edge is where the hours
   * actually end.
   */
  /*
   * The arrow follows the hand.
   *
   * These were pinned to the route's own planned start and end, so dragging an
   * arrow moved the pale band underneath while the arrow itself stayed where
   * it was -- the one thing being pointed at was the one thing that did not
   * respond. While an edge is being dragged the arrow, the thread and the band
   * all take the previewed time, so the lane visibly grows and shrinks.
   */
  /*
   * The arrows are the ends of the lane, and they move with the drag.
   *
   * They used to sit at the ends of the *work* -- where the first stop begins
   * and the last one finishes -- which made them a different thing from the
   * handles beside them, and dragging a handle left the arrow behind. Every
   * attempt to explain that was an explanation of why the thing being pointed
   * at would not follow the pointer.
   *
   * So they are the shift now: the start and end of the technician's day, which
   * is exactly what the drag sets. Pull one and it goes where it is pulled and
   * stays there, because that is now what was saved. The dashed thread runs
   * between them and stretches with them.
   */
  const from = pct(start);
  const to = pct(end);

  /* Where the work itself runs, drawn inside the day as a solid bar. */
  const workStarts = pct(route.actualStart ?? route.plannedStart);
  const workEnds = pct(route.projectedEnd ?? route.plannedEnd);
  const journey = route.stops.length > 0;

  const grip = (edge: 'start' | 'end') => ({
    onPointerDown: drag(edge),
    onPointerMove: move(edge),
    onPointerUp: (e: React.PointerEvent<HTMLElement>) => void release(edge)(e),
    title:
      edge === 'start'
        ? `Start of ${route.vehicleName}'s day — drag to change it for this date`
        : `End of ${route.vehicleName}'s day — drag to change it for this date`,
  });

  return (
    <>
      <span className="rp-shift" style={{ left: `${left}%`, width: `${width}%` }} />

      {/*
        * Drawn for every lane, including an empty one. The arrows are the
        * handles now, so a vehicle with no work still needs them -- that is the
        * lane somebody is about to make room on.
        */}
      <span
        className="rp-cap"
        data-end="start"
        data-grab={onShiftDay ? 'true' : undefined}
        style={{ left: `${from}%`, color }}
        {...(onShiftDay ? grip('start') : {})}
      />
      <span
        className="rp-thread"
        style={{ left: `${from}%`, width: `${Math.max(0, to - from)}%`, color }}
      />

      {/*
        * Where the work sits inside the day.
        *
        * The arrows and the dashed thread are the technician's hours now, which
        * left nothing saying where the round actually begins and ends. A solid
        * bar inside the dashed one: the day is what is available, the solid
        * part is what is planned into it, and the gap at either end is the slack
        * a dispatcher is looking for when deciding where another job could go.
        */}
      {journey ? (
        <span
          className="rp-work"
          style={{
            left: `${workStarts}%`,
            width: `${Math.max(0.3, workEnds - workStarts)}%`,
            color,
          }}
          title={`Work runs ${hhmm(route.actualStart ?? route.plannedStart)} to ${hhmm(
            route.projectedEnd ?? route.plannedEnd,
          )}`}
        />
      ) : null}
      <span
        className="rp-cap"
        data-end="finish"
        data-grab={onShiftDay ? 'true' : undefined}
        style={{ left: `calc(${to}% - 8px)`, color }}
        {...(onShiftDay ? grip('end') : {})}
      />

      {/*
        * Work that falls outside the hours, marked on the thread.
        *
        * Letting go of an arrow puts it back at the route's real end, because
        * that is still where the day finishes -- the new hours only bind the
        * next build. Without something saying so the drag looks rejected. This
        * is the part that would have to go: from the hours you set to where
        * the route actually runs.
        */}
      {journey && workEnds > to ? (
        <span
          className="rp-overrun"
          style={{ left: `${to}%`, width: `${workEnds - to}%` }}
          title="Work runs past the end of this day — rebuild to plan inside it"
        />
      ) : null}

      {/*
        * The band edge, in the same place as the arrow and doing the same thing.
        *
        * Not a second handle any more: both now sit on the end of the shift and
        * move together, so whichever one the pointer lands on, the same edge
        * moves. It is kept for the wider hit area and the visible tick -- an
        * eight-pixel arrowhead is a small target on a long ruler.
        *
        * It used to be conditional, and the condition was computed from live
        * positions: dragging the grip onto the arrow made them coincide, the
        * condition flipped, and React unmounted the element holding the pointer
        * capture. The drag died on its first move.
        */}
      {onShiftDay ? (
        <>
          <span
            className="rp-shift-grip"
            data-edge="start"
            style={{ left: `${left}%` }}
            {...grip('start')}
          />
          <span
            className="rp-shift-grip"
            data-edge="end"
            style={{ left: `${left + width}%` }}
            {...grip('end')}
          />
        </>
      ) : null}
      {preview ? (
        <span className="rp-shift-readout" style={{ left: `${pct(preview.seconds)}%` }}>
          {hhmm(preview.seconds)}
        </span>
      ) : null}
    </>
  );
}

function Row({
  route,
  unit,
  index,
  color,
  onShiftDay,
  secondsAtRatio,
  pct,
  now,
  isToday,
  selected,
  hidden,
  isDropTarget,
  dropAt,
  dropLabel,
  draggingStopId,
  draggable,
  onSelectRoute,
  onSelectStop,
  onOpenRoute,
  onToggleHidden,
  onToggleLock,
  onDragStart,
  onEdgeHold,
}: {
  route: BoardRoute;
  unit: 'mi' | 'km';
  index: number;
  color: string;
  onShiftDay?: (vehicleId: string | null, edge: 'start' | 'end', seconds: number) => Promise<void>;
  secondsAtRatio: (ratio: number) => number;
  pct: (seconds: number) => number;
  now: number;
  /** Whether the board shows today, so a missing check-in can be pointed out. */
  isToday: boolean;
  selected: boolean;
  hidden: boolean;
  isDropTarget: boolean;
  /** Percentage across the track where a dropped stop would land. */
  dropAt: number | null;
  /** The stop being dragged, previewed where it would land. */
  dropLabel: string | null;
  draggingStopId: string | null;
  draggable: boolean;
  onSelectRoute: (routeId: string) => void;
  onSelectStop: (routeId: string, stopId: string) => void;
  onOpenRoute: (routeId: string) => void;
  onEdgeHold: (edge: 'start' | 'end' | null) => void;
  onToggleHidden: (routeId: string) => void;
  onToggleLock: (routeId: string, locked: boolean) => void;
  onDragStart: (event: React.PointerEvent, stop: BoardStop) => void;
}) {
  const nowPct = pct(now);

  return (
    <div
      className="rp-row"
      data-route-id={laneKey(route)}
      data-selected={selected ? 'true' : undefined}
      data-drop-target={isDropTarget ? 'true' : undefined}
      /* Dimmed while something is being dragged: a lock means "not here". */
      data-locked={route.locked ? 'true' : undefined}
    >
      <div className="rp-cell rp-id">
        {/*
          * The vehicle's own number, not the row position. It is stable for the
          * life of the vehicle, it is the first half of every stop label, and a
          * dispatcher says "route 8" meaning this number -- a positional index
          * would rename every route whenever one was added or finished.
          *
          * Which is exactly what the old fallback did. A vehicle with no number
          * showed its row position, so it read as `1` while the vehicle that
          * really is 1 sat below it, and the badge changed every time the board
          * was re-sorted. A dash is the honest answer, and it points at the fix:
          * give the vehicle a number in Setup.
          */}
        <span
          className="rp-badge"
          style={{ borderColor: color, color }}
          title={
            routeNumberOf(route) === null
              ? `${route.vehicleName} has no vehicle number — set one in Setup to place it in this column`
              : undefined
          }
        >
          {routeNumberOf(route) ?? '—'}
        </span>
      </div>
      <button
        type="button"
        className="rp-cell rp-name rp-link"
        onClick={() => onSelectRoute(laneKey(route))}
        title={`${route.stops.length} stops · ${distance(route.plannedDistanceM, unit)} · ${hours(route.plannedDriveS)} driving`}
      >
        {route.vehicleName}
      </button>
      <button
        type="button"
        className="rp-cell rp-driver rp-link"
        onClick={() => onSelectRoute(laneKey(route))}
      >
        <CheckInBadge route={route} now={now} isToday={isToday} unit={unit} />
        <span className="rp-driver-name">{route.technicianName ?? '-'}</span>
      </button>

      <ViolationCell route={route} />

      {/*
        * The way into the route itself.
        *
        * It used to be a stop chip: clicking 2.1 opened the whole route and a
        * dispatcher after one customer's details got a list of twenty. The
        * chips now open the order they stand for, so the route needs a control
        * of its own, and a column beside the padlock is where the other
        * per-route actions already live.
        */}
      <button
        type="button"
        className="rp-cell rp-icon rp-toggle"
        disabled={!route.id}
        title={
          route.id ? `Open ${route.vehicleName}'s full route` : 'This vehicle has no route today'
        }
        onClick={() => route.id && onOpenRoute(laneKey(route))}
      >
        <RouteDetailIcon />
      </button>

      {/*
        * Coloured by state, not by hover.
        *
        * A padlock that is open or shut is a small difference in a 13px glyph,
        * read down a column of thirteen rows. Green open and red shut answers
        * "which of these is protected" from across the room; the shape still
        * carries it for anyone who cannot see the difference in hue.
        */}
      <button
        type="button"
        className="rp-cell rp-icon rp-toggle"
        data-lock={!route.id ? undefined : route.locked ? 'on' : 'off'}
        aria-pressed={route.locked}
        title={
          !route.id
            ? 'Nothing to lock: this vehicle has no route today'
            : route.locked
              ? 'Locked: a replan will not touch this route'
              : 'Lock this route'
        }
        disabled={!route.id}
        onClick={() => route.id && onToggleLock(route.id, !route.locked)}
      >
        <LockIcon locked={route.locked} />
      </button>
      <button
        type="button"
        className="rp-cell rp-icon rp-toggle"
        aria-pressed={!hidden}
        title={route.id ? (hidden ? 'Show on map' : 'Hide from map') : 'Nothing on the map yet'}
        disabled={!route.id}
        onClick={() => route.id && onToggleHidden(route.id)}
      >
        <EyeIcon hidden={hidden} />
      </button>

      <div className="rp-track">
        {/*
          * The hours this vehicle may work today, as a pale band, with a handle
          * at each end.
          *
          * Drawn first so everything else sits on top of it. Dragging an edge
          * says "this technician starts earlier today", which is a fact about
          * the day rather than about the vehicle -- the server writes it as a
          * calendar exception and the standing shift is left alone.
          */}
        <ShiftBand
          route={route}
          color={color}
          pct={pct}
          secondsAtRatio={secondsAtRatio}
          onShiftDay={onShiftDay}
          onEdgeHold={onEdgeHold}
        />
        {nowPct >= 0 && nowPct <= 100 ? <div className="rp-now" style={{ left: `${nowPct}%` }} /> : null}
        {/*
          * Where the stop would land, shown as the stop.
          *
          * A caret says "here"; a chip with the order on it says "this, here",
          * which is the question a dispatcher is actually asking while their
          * hand is moving. The caret stays underneath it as the precise edge.
          */}
        {dropAt !== null ? (
          <>
            <div className="rp-drop-caret" style={{ left: `${dropAt}%` }} />
            {dropLabel ? (
              <span className="rp-drop-preview" style={{ left: `${dropAt}%`, borderColor: color }}>
                {dropLabel}
              </span>
            ) : null}
          </>
        ) : null}
        {route.actualStart !== null ? (
          <span className="rp-drift">
            <Delta seconds={route.deltaSeconds} />
          </span>
        ) : null}
        {route.stops.map((stop, i) => (
          <Block
            key={stop.id}
            stop={stop}
            /* The badge's number, so a chip and its row cannot disagree. */
            routeIdx={route.routeNumber ?? route.vehicleIdx}
            pct={pct}
            color={color}
            previousDeparture={
              i === 0
                ? (route.actualStart ?? route.plannedStart)
                : (route.stops[i - 1]!.projectedDeparture ??
                   route.stops[i - 1]!.plannedDeparture)
            }
            dragging={draggingStopId === stop.id}
            movable={draggable && stop.status === 'pending'}
            onSelect={() => onSelectStop(laneKey(route), stop.id)}
            onDragStart={onDragStart}
          />
        ))}
      </div>
    </div>
  );
}

function Block({
  stop,
  routeIdx,
  pct,
  color,
  dragging,
  movable,
  previousDeparture,
  onSelect,
  onDragStart,
}: {
  stop: BoardStop;
  routeIdx: number | null;
  pct: (seconds: number) => number;
  color: string;
  dragging: boolean;
  movable: boolean;
  /** When the vehicle was free to set off for this stop. */
  previousDeparture: number;
  onSelect: () => void;
  onDragStart: (event: React.PointerEvent, stop: BoardStop) => void;
}) {
  const liveStart = stop.actualArrival ?? stop.detectedArrival ?? stop.projectedArrival ?? stop.plannedArrival;
  const liveEnd = stop.actualDeparture ?? stop.projectedDeparture ?? stop.plannedDeparture;

  const left = pct(liveStart);
  const width = Math.max(0.4, pct(liveEnd) - left);
  const ghostLeft = pct(stop.plannedArrival);
  const ghostWidth = Math.max(0.4, pct(stop.plannedDeparture) - ghostLeft);
  const drifted = Math.abs(stop.deltaSeconds ?? 0) >= 300;

  /* `8.1`, as stored on the stop -- see `stopLabel` for why not by position. */
  const ref = stopLabel(stop, routeIdx);
  const violations = stop.violations ?? [];
  const label =
    `${ref} · ${stop.orderName}` +
    (stop.notBefore !== null ? ` · held until ${hhmm(stop.notBefore)}` : '') +
    ` · plan ${hhmm(stop.plannedArrival)} · ` +
    `${stop.actualArrival !== null ? 'arrived' : 'eta'} ${hhmm(liveStart)}` +
    (movable ? ' · drag to reschedule' : '');

  const flipIfClipped = (event: { currentTarget: HTMLElement }) =>
    flipToFit(event.currentTarget, '.rp-stop-pop');

  /*
   * Idle time before this stop: the gap left once the drive is accounted for.
   * Travel comes from the plan rather than being inferred, so this separates
   * "too far away" from "too early to be let in".
   */
  const waiting = Math.max(0, liveStart - previousDeparture - (stop.travelSeconds ?? 0));
  const openAt =
    stop.timeWindows.find((w) => Math.abs(w.start - liveStart) < 120)?.start ?? null;

  return (
    <>
      {drifted ? (
        <div className="rp-ghost" style={{ left: `${ghostLeft}%`, width: `${ghostWidth}%` }} />
      ) : null}
      <button
        type="button"
        className="rp-stop"
        data-status={stop.status}
        /*
         * A held stop is marked. Sitting in the middle of a gap it is
         * indistinguishable from slack, and the next person to look at the
         * board would close the gap not knowing somebody opened it on purpose.
         */
        data-held={stop.notBefore !== null ? 'true' : undefined}
        data-risk={stop.windowRisk}
        data-dragging={dragging ? 'true' : undefined}
        data-movable={movable ? 'true' : undefined}
        style={{
          left: `${left}%`,
          width: `${width}%`,
          borderColor: stop.status === 'pending' ? color : undefined,
        }}
        title={label}
        aria-label={label}
        onPointerDown={(e) => {
          hideTip();
          if (movable) onDragStart(e, stop);
        }}
        onClick={onSelect}
        onPointerEnter={flipIfClipped}
        onFocus={flipIfClipped}
        onPointerLeave={hideTip}
        onBlur={hideTip}
        data-violated={violations.length > 0 ? 'true' : undefined}
      >
        <span className="rp-stop-ref">{ref}</span>

        {/*
          * The fact sheet their stop hover shows: the order, its position, its
          * status, and the three times with the drift under them. Ours was a
          * `title` string -- one run of plain text after a second of waiting,
          * which cannot group anything and cannot show a violation.
          */}
        <span className="rp-stop-pop" role="tooltip">
          <b>{stop.orderName}</b>
          <span className="rp-stop-grid">
            <em>ID</em>
            <em>Status</em>
            <span>{ref}</span>
            <span>{statusLabel(stop.status)}</span>
          </span>
          {/*
            * Why there is a gap before this stop, when there is one.
            *
            * A dispatcher looking at daylight between two chips wants to know
            * whether it is distance or a closed door, because only one of
            * those is theirs to fix. Waiting is what is left after the drive,
            * and when a window explains it the window is what to say.
            */}
          {waiting >= 300 ? (
            <span className="rp-stop-wait">
              Waits {Math.round(waiting / 60)} min
              {openAt !== null ? ` for the ${hhmm(openAt)} window` : ' before this stop'}
            </span>
          ) : null}
          <span className="rp-stop-grid rp-stop-times">
            <em>Arrival</em>
            <em>Service</em>
            <em>Departure</em>
            <span>
              {hhmm(liveStart)}
              {stop.deltaSeconds ? <i>{signed(stop.deltaSeconds)}</i> : null}
            </span>
            <span>{Math.round(stop.serviceTime / 60)} min</span>
            <span>
              {hhmm(stop.actualDeparture ?? stop.projectedDeparture ?? stop.plannedDeparture)}
              {stop.deltaSeconds ? <i>{signed(stop.deltaSeconds)}</i> : null}
            </span>
          </span>
          {violations.length > 0 ? (
            <>
              <em className="rp-stop-viol-head">Violations</em>
              {violations.map((v, i) => (
                <span key={i} className="rp-stop-viol">
                  {v.label}
                </span>
              ))}
            </>
          ) : null}
        </span>
      </button>
    </>
  );
}

/**
 * How many things are wrong with this route, and what they are.
 *
 * A count on the row and the detail on hover, which is how the incumbent does
 * it and is right: ten routes are scanned at a glance and only the bad one is
 * read. Zero is drawn quietly rather than hidden, so the column does not jump
 * about as a day develops.
 *
 * The panel is ours rather than a `title` attribute because the two kinds are
 * grouped under headings, and a native tooltip is one run of plain text after
 * a second of waiting.
 */
function ViolationCell({ route }: { route: BoardRoute }) {
  const counts = route.violationCounts ?? { vehicle: 0, order: 0, total: 0 };
  const violations = route.violations ?? [];
  const vehicle = violations.filter((v) => v.scope === 'vehicle');
  const order = violations.filter((v) => v.scope === 'order');

  return (
    <div className="rp-cell rp-violations">
      <span
        className="rp-viol"
        onPointerEnter={(e) => flipToFit(e.currentTarget, '.rp-viol-pop')}
        onFocus={(e) => flipToFit(e.currentTarget, '.rp-viol-pop')}
        onPointerLeave={hideTip}
        onBlur={hideTip}
        data-empty={counts.total === 0 ? 'true' : undefined}
        tabIndex={counts.total > 0 ? 0 : undefined}
        aria-label={
          counts.total === 0
            ? 'No violations'
            : `${counts.vehicle} vehicle and ${counts.order} order violations`
        }
      >
        {counts.total}
        {counts.total > 0 ? (
          <span className="rp-viol-pop" role="tooltip">
            <b>{route.vehicleName}</b>
            <em>Violations</em>
            {vehicle.length > 0 ? (
              <>
                <span className="rp-viol-group">
                  {vehicle.length} vehicle violation{vehicle.length === 1 ? '' : 's'}
                </span>
                {vehicle.map((v, i) => (
                  <span key={`v${i}`} className="rp-viol-line">
                    {v.message}
                  </span>
                ))}
              </>
            ) : null}
            {order.length > 0 ? (
              <>
                <span className="rp-viol-group">
                  {order.length} order violation{order.length === 1 ? '' : 's'}
                </span>
                {order.map((v, i) => (
                  <span key={`o${i}`} className="rp-viol-line">
                    {v.message}
                  </span>
                ))}
              </>
            ) : null}
          </span>
        ) : null}
      </span>
    </div>
  );
}

/** `+38 min`, as theirs reads it, under the time it belongs to. */
function signed(seconds: number): string {
  const minutes = Math.round(seconds / 60);
  return `${minutes > 0 ? '+' : ''}${minutes} min`;
}

function WarningIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 2.2 14.4 13.4H1.6L8 2.2Z"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
      <path d="M8 6.4v3.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="8" cy="11.4" r="0.7" fill="currentColor" />
    </svg>
  );
}

/*
 * Open and shut, told apart at 13 pixels.
 *
 * The open shackle used to be the shut one with its right leg trimmed by a
 * pixel and a half: both drew a full arch ending level with where it began, and
 * at this size nobody could see which was which -- the operator read every row
 * as locked. The open one now stops short of coming down and lifts away to the
 * right, so the silhouette differs rather than a detail of it. Colour is a
 * second signal, not the only one.
 */
/*
 * Open and shut, told apart at 13 pixels.
 *
 * Twice now the open padlock has been the shut one with a bite taken out of
 * it: first a leg shortened by a pixel and a half, then an arc stopping early.
 * Both left the same silhouette -- a shackle centred over the body -- and at
 * this size a silhouette is all there is, so every row read as locked.
 *
 * The shackle now swings clear of the body instead. Shut, it sits centred with
 * both legs down; open, it is hinged at the right and stands off to one side,
 * leaving the top-left of the body visibly bare. That is a difference in the
 * shape of the whole glyph rather than in a detail of it, which is the only
 * kind that survives being this small. Colour is the second signal, not the
 * only one.
 */
function LockIcon({ locked }: { locked: boolean }) {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect
        x="1.8"
        y="6.2"
        width="8.4"
        height="6.3"
        rx="1.2"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <path
        d={
          locked
            ? /* Centred over the body, both legs down. */
              'M4 6.2V4.3a2 2 0 0 1 4 0V6.2'
            : /* Hinged right, standing clear, free end in the air. */
              'M8.2 6.2V4.3a2 2 0 0 1 4 0'
        }
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/*
 * The route, as a list of stops with times beside them. Deliberately not a
 * magnifier or a chevron: both read as "more of this row", and what this opens
 * is a different thing -- the technician's whole day.
 */
function RouteDetailIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M2 3.4h6M2 7h6M2 10.6h6"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
      <path
        d="M10.4 3.4h1.6M10.4 7h1.6M10.4 10.6h1.6"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        opacity="0.45"
      />
    </svg>
  );
}

function EyeIcon({ hidden }: { hidden: boolean }) {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M1.5 8s2.4-4 6.5-4 6.5 4 6.5 4-2.4 4-6.5 4S1.5 8 1.5 8z"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <circle cx="8" cy="8" r="1.7" stroke="currentColor" strokeWidth="1.3" />
      {hidden ? <path d="M2.5 13.5l11-11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /> : null}
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Check-in
 * ------------------------------------------------------------------ */

const SOURCE_LABEL: Record<string, string> = {
  driver_app: 'From the driver app',
  dispatcher: 'Entered by a dispatcher',
  api: 'Reported through the API',
  geofence: 'Detected by location',
  import: 'Imported',
};


function minutesText(diff: number): string {
  const m = Math.round(Math.abs(diff) / 60);
  if (m === 0) return 'on time';
  const len = m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
  return diff < 0 ? `${len} early` : `${len} late`;
}

/**
 * Whether the driver has checked in, beside their name: a green tick once
 * they have, amber while on a break, grey once checked out, and -- on today's
 * board, once the planned start has passed -- a hollow marker saying they
 * have not. Hovering gives the details.
 */
function CheckInBadge({
  route,
  now,
  isToday,
  unit,
}: {
  route: BoardRoute;
  now: number;
  isToday: boolean;
  unit: 'mi' | 'km';
}) {
  const a = route.attendance ?? null;
  const checkedIn = a?.checkedInAt !== null && a?.checkedInAt !== undefined;
  const missing = !checkedIn && isToday && route.id !== null && route.technicianName !== null && now >= route.plannedStart;
  if (!checkedIn && !missing) return null;

  const state = !checkedIn ? 'missing' : a!.checkedOutAt !== null ? 'out' : a!.onBreak ? 'break' : 'in';
  /* Late is a minute or more after the planned start; anything earlier is on time. */
  const lateBy = checkedIn ? a!.checkedInAt! - route.plannedStart : 0;
  const late = checkedIn && lateBy >= 60;
  const label =
    state === 'missing'
      ? 'Not checked in'
      : state === 'out'
        ? `Checked out at ${ampm(a!.checkedOutAt!)}`
        : state === 'break'
          ? 'On a break'
          : `Checked in at ${ampm(a!.checkedInAt!)}`;

  return (
    <span
      className="rp-checkin"
      data-state={state}
      data-late={late ? 'true' : undefined}
      aria-label={label}
      onPointerEnter={(e) => flipToFit(e.currentTarget, '.rp-checkin-pop')}
      onPointerLeave={hideTip}
    >
      {state === 'missing' ? (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
          <circle cx="8" cy="8" r="6.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.4 2" />
        </svg>
      ) : state === 'break' ? (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path d="M6.2 5v6M9.8 5v6" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
          <circle cx="8" cy="8" r="7" fill="currentColor" />
          <path d="M4.8 8.2l2.2 2.2 4.2-4.6" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      <span className="rp-checkin-pop" role="tooltip">
        <b>{route.technicianName ?? 'Driver'}</b>
        {state === 'missing' ? (
          <>
            <span className="rp-checkin-status" data-tone="warn">Not checked in yet</span>
            <span>
              Planned start {ampm(route.plannedStart)}, {minutesText(now - route.plannedStart).replace(' late', ' ago')}.
            </span>
            <span className="rp-checkin-dim">Shows here as soon as the driver checks in from the app.</span>
          </>
        ) : (
          <>
            <span className="rp-checkin-status" data-tone={late ? 'late' : 'ok'}>
              Checked in at {ampm(a!.checkedInAt!)}
              {a!.checkedInIso
                ? ` on ${new Date(a!.checkedInIso).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}`
                : ''}
            </span>
            <span className="rp-checkin-status" data-tone={late ? 'late' : 'ok'}>
              {late || lateBy <= -60 ? minutesText(lateBy) : 'On time'} · planned start {ampm(route.plannedStart)}
            </span>
            <span>
              {SOURCE_LABEL[a!.checkedInSource ?? ''] ?? 'Reported'}
              {a!.device ? ` on ${a!.device}` : ''}.
            </span>
            <span>
              {a!.checkedInMetersFromDepot !== null
                ? `${distance(a!.checkedInMetersFromDepot, unit)} from ${route.startDepot?.name ?? 'the depot'}.`
                : a!.checkedInLocation
                  ? 'Location sent with the check-in.'
                  : 'No location was sent with the check-in.'}
            </span>
            {a!.breaks > 0 || a!.onBreak ? (
              <span>
                {a!.onBreak ? 'On a break now. ' : ''}
                {a!.breaks} break{a!.breaks === 1 ? '' : 's'} today.
              </span>
            ) : null}
            {a!.checkedOutAt !== null ? <span>Checked out at {ampm(a!.checkedOutAt)}.</span> : null}
          </>
        )}
      </span>
    </span>
  );
}
